import { BehaviorSubject, combineLatest, map, Subject } from 'rxjs';
import { CompanyModuleName } from '../models/company-minimal-model';
import { Injectable } from '@angular/core';
/** Models */
import { UserModel } from '../models/user-model';
import {
  ACCOUNTS_PAYABLE_MENU,
  ApNavGroup,
  CompanyPageSubMenu,
  CompanyPermission,
  ExpenseNavGroup,
  PageNavGroup,
} from '../models/company-permission.model';
import {
  ANNOUNCEMENT_ANY_AUTHOR_ROLE_IDS,
  ANNOUNCEMENT_AUTHOR_ROLE_IDS,
  RoleConstant,
} from '../models/role-constant';
import { AppPermissions, ModulePermission } from '../models/user-permission-model';
import {
  INTEGRATION_DATA_PAGE_URL,
  INTEGRATION_DATA_PERMISSION_MODULE,
  IntegrationDataAction,
} from '../models/integration-data.model';
import { ORGANIZATION_SETTINGS_PAGE_URL } from '../models/organization.model';
import { ROLE_PERMISSION_MODULE, RoleAction } from '../models/role.model';
import { ROLE_PERMISSIONS_MAP } from '../utils/role-permissions-map';
import { ROLE_ID_TO_NAME_MAP } from '../utils/role-id-to-name.map';
import { canUserAccessCollatedPayment, isBypassedApManager1ActionBlocked } from '../utils/ap-manager-bypass.util';
import {
  getAccountsPayableNavGroups,
  getAccountsPayableSubMenus,
  getCompanyPermissionForCompany,
  getExpenseNavGroup,
  getHomepageNavGroup,
  getMealDynamicsAiNavGroup,
  getStoredApRoleCompanyPermissions,
  hasExpenseSidebar,
  hasPageUrlInAnyMenu,
  hasPageUrlInMenu,
  isApListRoutePath,
  normalizeRoutePath,
  resolveActiveCompanyId,
  resolveApPageTitleFromSubMenus,
  resolveBePageUrlAllowed,
  resolveSidebarPageUrlAllowed,
  storeApRoleCompanyPermissions,
  usesApiPagePermissions,
} from '../utils/company-permission.util';
import { resolveAccountsPayableActionAllowed, getLegacyAccountsPayableActionAllowed, hasAccountsPayableModuleActionPermissions } from '../utils/accounts-payable-action-permission.util';
import { resolveExpenseActionAllowed } from '../utils/expense-action-permission.util';
import {
  resolveRmActionAllowed,
} from '../utils/rm-action-permission.util';
import { RmActionName } from '../models/rm-actions.model';
import { AccountsPayableAction, AccountsPayableActionName } from '../models/accounts-payable-actions.model';
import { ExpenseActionName } from '../models/expense-actions.model';

@Injectable({
  providedIn: 'root',
})
export class ContextService {
  public user: BehaviorSubject<UserModel> = new BehaviorSubject<UserModel>(
    undefined
  );

  public multipleUsers: BehaviorSubject<UserModel> = new BehaviorSubject<UserModel>(
    undefined
  );

  public selectedRoleId$ = new BehaviorSubject<string | null>(null);
  private companyIdSource = new BehaviorSubject<string | null>(null);
  selectedCompanyId$ = this.companyIdSource.asObservable();

  /** Company config: when true, AP Manager 1 is bypassed for AP workflows. */
  readonly bypassApManager1$ = new BehaviorSubject<boolean>(false);

  /** Fired from the header role switcher so dashboard can refetch Module/Search for the new role. */
  private readonly roleSwitch$ = new Subject<void>();
  readonly onRoleSwitch$ = this.roleSwitch$.asObservable();


  roles$ = this.user.pipe(
    map((user) => (user.userRoles || []).map((role) => role.roleId))
  );

  isAPCreator$ = this.roles$.pipe(
    map((roleIds) => roleIds.includes(RoleConstant.accountsPayableCreator))
  );
  isAPManger1$ = this.roles$.pipe(
    map((roleIds) => roleIds.includes(RoleConstant.accountsPayableManager1))
  );
  /** AP Manager 1 role that is still active (not bypassed by company config). */
  isActiveAPManger1$ = combineLatest([
    this.isAPManger1$,
    this.bypassApManager1$,
  ]).pipe(map(([isAP1, bypass]) => isAP1 && !bypass));
  isAPManger2$ = this.roles$.pipe(
    map((roleIds) => roleIds.includes(RoleConstant.accountsPayableManager2))
  );
  isAuditor1$ = this.roles$.pipe(
    map((roleIds) => roleIds.includes(RoleConstant.auditor1))
  );
  isAuditor2$ = this.roles$.pipe(
    map((roleIds) => roleIds.includes(RoleConstant.auditor2))
  );
  isCompanyAdmin$ = this.roles$.pipe(
    map((roleIds) => roleIds.includes(RoleConstant.companyAdminId))
  );

  /** Roles that may use announcements admin list and CRUD (company-wide and/or hierarchy-scoped). */
  canManageAnnouncements$ = this.roles$.pipe(
    map((roleIds) => roleIds.some((id) => ANNOUNCEMENT_ANY_AUTHOR_ROLE_IDS.includes(id)))
  );

  /** Company Admin, AP Manager 1/2, or Auditor 2 — may choose company-wide announcements. */
  canAnnounceCompanyWide$ = this.roles$.pipe(
    map((roleIds) => roleIds.some((id) => ANNOUNCEMENT_AUTHOR_ROLE_IDS.includes(id)))
  );
  isVendor$ = this.roles$.pipe(
    map((roleIds) => roleIds.includes(RoleConstant.vendorId))
  );
  isGlobalAdmin$ = this.roles$.pipe(
    map((roleIds) => roleIds.includes(RoleConstant.globalAdminId))
  );

  isvpOperations$ = this.roles$.pipe(
    map((roleIds) => roleIds.includes(RoleConstant.vpOperations))
  );
  isSrDirectorOperations$ = this.roles$.pipe(
    map((roleIds) => roleIds.includes(RoleConstant.srDirectorOperations))
  );
  isdirectorOperations$ = this.roles$.pipe(
    map((roleIds) => roleIds.includes(RoleConstant.directorOperations))
  );
  isSupervisor$ = this.roles$.pipe(
    map((roleIds) => roleIds.includes(RoleConstant.supervisor))
  );

  isGeneralManager$ = this.roles$.pipe(
    map((roleIds) => roleIds.includes(RoleConstant.generalManager))
  );

  isEmployee$ = this.roles$.pipe(
    map((roleIds) => roleIds.includes(RoleConstant.employeeId))
  );
  isExpenseInitiator$ = this.roles$.pipe(
    map((roleIds) => roleIds.includes(RoleConstant.expenseInitiator))
  );

  // Multi-Creator Role
  isMultiCreator$ = combineLatest([
    this.isAuditor1$,
    this.isAuditor2$,
    this.isvpOperations$,
    this.isSrDirectorOperations$,
    this.isdirectorOperations$,
    this.isSupervisor$,
    this.isGeneralManager$,
    this.isEmployee$,
    this.isExpenseInitiator$,
  ]).pipe(
    map(([aud1, aud2, vpOps, srDir, dirOps, sup, gm, emp, expInt]) =>
      aud1 || aud2 || vpOps || srDir || dirOps || sup || gm || emp || expInt
    )
  );

  isGlobalAdmin(): boolean {
    const roleIds = this.user.value?.userRoles?.map((r) => r.roleId) ?? [];
    return roleIds.includes(RoleConstant.globalAdminId);
  }

  setSelectedRoleId(roleId: string | null) {
    this.selectedRoleId$.next(roleId);
    if (roleId) {
      localStorage.setItem('selectedRoleId', roleId);
    } else {
      localStorage.removeItem('selectedRoleId');
    }
  }

  /** Call after changing the active role from the switcher (triggers dashboard Module/Search + layout reload). */
  notifyRoleSwitch(): void {
    this.roleSwitch$.next();
  }


  loadInitialSelectedRoleId() {
    const savedRole = localStorage.getItem('selectedRoleId');
    if (savedRole) {
      this.selectedRoleId$.next(savedRole);
    }
  }

  setCompanyId(id: string) {
    this.companyIdSource.next(id);

    if (id) {
      localStorage.setItem('selectedCompanyId', id);
    } else {
      localStorage.removeItem('selectedCompanyId');
    }
  }

  getCompanyId(): string | null {
    return this.companyIdSource.value;
  }

  setBypassApManager1(enabled: boolean): void {
    this.bypassApManager1$.next(enabled);
  }

  isBypassApManager1Enabled(): boolean {
    return this.bypassApManager1$.value;
  }

  canAccessCollatedPayment(): boolean {
    const stored = this.getStoredPermissionsForSelectedRole();
    const companyId = this.getActiveCompanyId();
    const companyPermission = getCompanyPermissionForCompany(stored, companyId);

    // BE-driven when /my sent pagePermissions or AP actionPermissions.
    if (
      usesApiPagePermissions(companyPermission) ||
      hasAccountsPayableModuleActionPermissions(stored, companyId)
    ) {
      if (
        this.isAccountsPayableActionAllowed(AccountsPayableAction.CollatedPayment)
      ) {
        return true;
      }

      if (usesApiPagePermissions(companyPermission)) {
        return hasPageUrlInMenu(
          companyPermission,
          ACCOUNTS_PAYABLE_MENU,
          'accounts-payable/collated-payment'
        );
      }

      return false;
    }

    return canUserAccessCollatedPayment(
      this.getSelectedRoleId(),
      this.isBypassApManager1Enabled()
    );
  }

  hasAccountsPayableSidebar(): boolean {
    return this.getAccountsPayableNavGroups().some(
      (group) => group.subMenus.length > 0
    );
  }

  getAccountsPayableNavGroups(): ApNavGroup[] {
    return getAccountsPayableNavGroups(
      this.getStoredPermissionsForSelectedRole(),
      this.getActiveCompanyId(),
      this.getSelectedRoleId(),
      this.isBypassApManager1Enabled()
    );
  }

  getAccountsPayableNavGroup(): ApNavGroup | null {
    return this.getAccountsPayableNavGroups()[0] ?? null;
  }

  getAccountsPayableSubMenus(): CompanyPageSubMenu[] {
    return getAccountsPayableSubMenus(
      this.getStoredPermissionsForSelectedRole(),
      this.getActiveCompanyId(),
      this.getSelectedRoleId(),
      this.isBypassApManager1Enabled()
    );
  }

  resolveAccountsPayablePageTitle(
    pathname: string,
    currentQuery?: URLSearchParams | null
  ): string | null {
    const subMenus = this.getAccountsPayableSubMenus();
    if (!subMenus.length) {
      return null;
    }

    const normalizedPath = normalizeRoutePath(pathname.split('?')[0]);
    // List tabs + bill view/detail share this path family; always show the module
    // name (never a tab/submenu label like "For Approval").
    if (isApListRoutePath(normalizedPath)) {
      return 'Accounts Payable';
    }

    return resolveApPageTitleFromSubMenus(
      pathname,
      subMenus,
      currentQuery
    );
  }

  saveApRoleCompanyPermissions(
    roleId: string,
    companyPermissions: CompanyPermission[] | undefined
  ): void {
    storeApRoleCompanyPermissions(roleId, companyPermissions);
    this.apRolePermissionsChanged$.next(roleId);
  }

  getStoredPermissionsForRole(roleId: string | null | undefined) {
    return getStoredApRoleCompanyPermissions(roleId);
  }

  hasPageUrlAccess(pageUrl: string, menu = ACCOUNTS_PAYABLE_MENU): boolean {
    const companyPermission = this.getActiveCompanyPermission();
    if (!usesApiPagePermissions(companyPermission)) {
      return false;
    }

    return hasPageUrlInMenu(companyPermission, menu, pageUrl);
  }

  /**
   * Sidebar item visibility. BE pagePermissions grant a URL when listed; if the
   * URL is omitted (or pagePermissions are missing), legacyAllowed applies.
   */
  canShowSidebarPageUrl(pageUrl: string, legacyAllowed: boolean): boolean {
    return resolveSidebarPageUrlAllowed(
      this.getStoredPermissionsForSelectedRole(),
      this.getActiveCompanyId(),
      pageUrl,
      legacyAllowed
    );
  }

  /** Like canShowSidebarPageUrl, but BE-only: a URL BE pagePermissions omit is hidden (used for Organizations). */
  canShowBePageUrl(pageUrl: string, legacyAllowed: boolean): boolean {
    return resolveBePageUrlAllowed(
      this.getStoredPermissionsForSelectedRole(),
      this.getActiveCompanyId(),
      pageUrl,
      legacyAllowed
    );
  }

  canModuleAction(module: string, action: string): boolean {
    const companyPermission = this.getActiveCompanyPermission();
    const match = companyPermission?.actionPermissions?.find(
      (permission) =>
        permission.module === module &&
        permission.action === action &&
        permission.isAllowed
    );

    return Boolean(match);
  }

  /** Integration data & import jobs: tab access, from the Organization action permissions. */
  canIntegrationDataTab(tab: 'restlets' | 'import-jobs'): boolean {
    return this.canModuleAction(
      INTEGRATION_DATA_PERMISSION_MODULE,
      tab === 'restlets' ? IntegrationDataAction.Restlets : IntegrationDataAction.ImportJobs
    );
  }

  /** Integration data & import jobs is fully BE-driven: the page permission plus at least one tab's action. */
  canOpenIntegrationData(): boolean {
    return (
      hasPageUrlInAnyMenu(this.getActiveCompanyPermission(), INTEGRATION_DATA_PAGE_URL) &&
      (this.canIntegrationDataTab('restlets') || this.canIntegrationDataTab('import-jobs'))
    );
  }

  /**
   * True when the selected role is System Admin. Organization-level actions (Enter / Deactivate Organization,
   * Add / Remove Module) are for the System Admin only; other roles view and edit their organization.
   */
  isSystemAdminRole(): boolean {
    return this.getSelectedRoleId() === RoleConstant.globalAdminId;
  }

  /** Organization Settings: the OrganizationSettings page permission plus the Organization View action. */
  canOpenOrganizationSettings(): boolean {
    return (
      hasPageUrlInAnyMenu(this.getActiveCompanyPermission(), ORGANIZATION_SETTINGS_PAGE_URL) &&
      this.canModuleAction('Organization', 'View')
    );
  }

  /**
   * Roles page: edit permissions and title — the Role Update action from /my. The System Admin inside an
   * organization may too (the portal can't open Roles at all).
   */
  canEditRoles(): boolean {
    return (
      this.canModuleAction(ROLE_PERMISSION_MODULE, RoleAction.Update) ||
      this.getSelectedRoleId() === RoleConstant.globalAdminId
    );
  }

  /** A role can't edit its own permissions (Figma 08). */
  isOwnRole(roleId: string): boolean {
    return this.getSelectedRoleId() === roleId;
  }

  isAccountsPayableActionAllowed(
    action: AccountsPayableActionName | string,
    legacyAllowed?: boolean
  ): boolean {
    const roleIds =
      this.user.value?.userRoles?.map((role) => role.roleId) ?? [];
    const selectedRoleId = this.getSelectedRoleId();
    const bypassApManager1 = this.isBypassApManager1Enabled();
    const resolvedLegacy =
      legacyAllowed ??
      getLegacyAccountsPayableActionAllowed(action, {
        roleIds,
        can: (module, permissionAction) => this.can(module, permissionAction),
        bypassApManager1,
      });

    const allowed = resolveAccountsPayableActionAllowed(
      this.getStoredPermissionsForSelectedRole(),
      this.getActiveCompanyId(),
      selectedRoleId,
      action,
      resolvedLegacy
    );

    // BE may still grant list.approve for AP Manager 1; company bypass skips that role.
    if (
      allowed &&
      isBypassedApManager1ActionBlocked(
        selectedRoleId,
        bypassApManager1,
        action
      )
    ) {
      return false;
    }

    return allowed;
  }

  hasExpenseSidebar(): boolean {
    return hasExpenseSidebar(
      this.getStoredPermissionsForSelectedRole(),
      this.getActiveCompanyId(),
      this.getSelectedRoleId()
    );
  }

  getExpenseNavGroup(): ExpenseNavGroup | null {
    return getExpenseNavGroup(
      this.getStoredPermissionsForSelectedRole(),
      this.getActiveCompanyId(),
      this.getSelectedRoleId()
    );
  }

  getHomepageNavGroup(): PageNavGroup | null {
    return getHomepageNavGroup(
      this.getStoredPermissionsForSelectedRole(),
      this.getActiveCompanyId()
    );
  }

  getMealDynamicsAiNavGroup(): PageNavGroup | null {
    return getMealDynamicsAiNavGroup(
      this.getStoredPermissionsForSelectedRole(),
      this.getActiveCompanyId()
    );
  }

  isExpenseActionAllowed(
    action: ExpenseActionName | string
  ): boolean {
    return resolveExpenseActionAllowed(
      this.getStoredPermissionsForSelectedRole(),
      this.getActiveCompanyId(),
      action
    );
  }

  isRmActionAllowed(
    action: RmActionName | string,
    legacyAllowed = false
  ): boolean {
    return resolveRmActionAllowed(
      this.getStoredPermissionsForSelectedRole(),
      this.getActiveCompanyId(),
      action,
      legacyAllowed
    );
  }

  private readonly apRolePermissionsChanged$ = new Subject<string>();
  readonly onApRolePermissionsChanged$ = this.apRolePermissionsChanged$.asObservable();

  private getStoredPermissionsForSelectedRole() {
    return getStoredApRoleCompanyPermissions(this.getSelectedRoleId());
  }

  /** Company used for BE companyPermissions lookup (role company, then selected company). */
  /**
   * Whether a module is Active for the active company, from `userRoles[].company.modules` in /me.
   * Inactive or Removed modules are hidden from the sidebar, Quick Actions and routes. When /me has
   * no module list (older companies, System Admin), every module counts as active.
   */
  isCompanyModuleActive(module: CompanyModuleName): boolean {
    const roles = this.multipleUsers.value?.userRoles ?? this.user.value?.userRoles ?? [];
    const companyId = this.getActiveCompanyId();
    const company =
      roles.find((r) => r.company?.id && r.company.id === companyId)?.company ??
      roles.find((r) => r.company?.modules?.length)?.company;
    const modules = company?.modules;
    if (!modules?.length) return true;
    const entry = modules.find((m) => m.module === module);
    return !entry || entry.status === 'Active';
  }

  /**
   * Whether a module sync (e.g. `ExpensesToAccountsPayable`) is on for the active company, from
   * `userRoles[].company.syncs` in /me. With no sync list or entry (older companies), it counts as on,
   * so the existing flow stays.
   */
  isCompanySyncActive(sync: string): boolean {
    const roles = this.multipleUsers.value?.userRoles ?? this.user.value?.userRoles ?? [];
    const companyId = this.getActiveCompanyId();
    const company =
      roles.find((r) => r.company?.id && r.company.id === companyId)?.company ??
      roles.find((r) => r.company?.syncs?.length)?.company;
    const entry = company?.syncs?.find((s) => s.sync === sync);
    return entry ? entry.isActive === true : true;
  }

  /**
   * Accounting platform of the active company from /me (`company.platform`), or null when it has none
   * (`None` or missing), e.g. for "Integrate to {platform}" on AP.
   */
  getCompanyPlatform(): string | null {
    const roles = this.multipleUsers.value?.userRoles ?? this.user.value?.userRoles ?? [];
    const companyId = this.getActiveCompanyId();
    const company =
      roles.find((r) => r.company?.id && r.company.id === companyId)?.company ??
      roles.find((r) => r.company?.platform)?.company;
    const platform = (company?.platform ?? '').trim();
    return platform && platform.toLowerCase() !== 'none' ? platform : null;
  }

  getActiveCompanyId(): string | null {
    return resolveActiveCompanyId(
      this.user.value?.userRoles,
      this.getCompanyId()
    );
  }

  private getActiveCompanyPermission() {
    return getCompanyPermissionForCompany(
      this.getStoredPermissionsForSelectedRole(),
      this.getActiveCompanyId()
    );
  }

  private getSelectedRoleId(): string | null {
    return (
      this.selectedRoleId$.value ??
      (typeof localStorage !== 'undefined'
        ? localStorage.getItem('selectedRoleId')
        : null)
    );
  }

  /** Timezone from logged-in user's company (userRoles[0].company.timeZone). Fallback: Eastern Standard Time. */
  getUserCompanyTimezone(): string {
    const tz = this.user.value?.userRoles?.[0]?.company?.timeZone;
    return tz ?? 'Eastern Standard Time';
  }

  getPermissions(): AppPermissions {
    const roleIds =
      Array.from(
        new Set(
          this.user.value?.userRoles?.map(r => r.roleId) ?? []
        )
      );

    const result: Partial<AppPermissions> = {};

    roleIds.forEach(roleId => {
      const roleName = ROLE_ID_TO_NAME_MAP[roleId];
      if (!roleName) return;

      const rolePerms = ROLE_PERMISSIONS_MAP[roleName] ?? {};

      (Object.keys(rolePerms) as Array<keyof AppPermissions>).forEach(moduleKey => {
        if (!result[moduleKey]) {
          result[moduleKey] = {
            view: false,
            create: false,
            edit: false,
            delete: false
          };
        }

        const perms = rolePerms[moduleKey];
        if (perms) {
          (Object.keys(perms) as Array<keyof ModulePermission>).forEach(action => {
            result[moduleKey][action] ||= perms[action];
          });
        }
      });
    });

    // Default structure
    const defaultModules: AppPermissions = {
      user: { view: false, create: false, edit: false, delete: false },
      store: { view: false, create: false, edit: false, delete: false },
      storeGroup: { view: false, create: false, edit: false, delete: false },
      company: { view: false, create: false, edit: false, delete: false },
      ap: { view: false, create: false, edit: false, delete: false },
      asset: { view: false, create: false, edit: false, delete: false },
      expense: { view: false, create: false, edit: false, delete: false },
      RM: { view: false, create: false, edit: false, delete: false },
      approvalProcess: { view: false, create: false, edit: false, delete: false },
      vendors: { view: false, create: false, edit: false, delete: false },
    };

    return { ...defaultModules, ...result };
  }



  can(module: keyof AppPermissions, action: keyof ModulePermission): boolean {
    const perms = this.getPermissions();
    return perms?.[module]?.[action] ?? false;
  }

}
